import L from "leaflet";
import maplibregl from "maplibre-gl";

const DEFAULT_ZOOM_OFFSET = 0;
const MIN_ROTATION_OVERSCAN_PX = 256;
const ROTATION_OVERSCAN_EXTRA_PX = 128;

function inferZoomOffset(style, fallback = DEFAULT_ZOOM_OFFSET) {
  const sources = style?.sources ? Object.values(style.sources) : [];
  const source = sources.find((s) => s && typeof s === "object") || null;
  const tileSize = source?.tileSize || 0;
  if (tileSize === 256) return 0;
  if (tileSize === 512) return 1;
  return fallback;
}

function computeContainerGeometry(size, rotationOverscan) {
  const viewportWidth = Math.max(0, Number(size?.x) || 0);
  const viewportHeight = Math.max(0, Number(size?.y) || 0);
  if (!rotationOverscan || !viewportWidth || !viewportHeight) {
    return {
      width: viewportWidth,
      height: viewportHeight,
      padX: 0,
      padY: 0,
    };
  }

  const diagonal = Math.ceil(Math.hypot(viewportWidth, viewportHeight));
  const padX = Math.max(
    MIN_ROTATION_OVERSCAN_PX,
    Math.ceil((diagonal - viewportWidth) / 2) + ROTATION_OVERSCAN_EXTRA_PX
  );
  const padY = Math.max(
    MIN_ROTATION_OVERSCAN_PX,
    Math.ceil((diagonal - viewportHeight) / 2) + ROTATION_OVERSCAN_EXTRA_PX
  );
  return {
    width: viewportWidth + padX * 2,
    height: viewportHeight + padY * 2,
    padX,
    padY,
  };
}

export function createMapLibreLayer({
  style,
  className = "leaflet-maplibre-layer",
  zoomOffset,
  rotationOverscan = false,
  pixelRatio,
  maxTileCacheSize,
  maxTileCacheZoomLevels,
  fadeDuration,
  interactionUpdateInterval = 0,
  zIndex,
} = {}) {
  const resolvedZoomOffset =
    typeof zoomOffset === "number" ? zoomOffset : inferZoomOffset(style);
  const MapLibreLayer = L.Layer.extend({
    initialize(options) {
      L.setOptions(this, options);
      this._glMap = null;
      this._container = null;
      this._map = null;
      this._glReady = false;
      this._center = null;
      this._zoom = null;
      this._containerGeometry = { width: 0, height: 0, padX: 0, padY: 0 };
      this._canvasContextLostHandler = null;
      this._canvasContextRestoredHandler = null;
      this._suspended = false;
      this._visibilityHandler = null;
      this._appStateHandle = null;
      this._updateFrame = null;
      this._settleTimer = null;
      this._lastInteractionUpdateAt = 0;
    },

    onAdd(map) {
      this._map = map;
      this._container = L.DomUtil.create("div", className);
      this._container.style.position = "absolute";
      this._container.style.top = "0";
      this._container.style.left = "0";
      this._container.style.pointerEvents = "none";
      this._container.style.transformOrigin = "0 0";
      this._container.style.background = "transparent";
      if (Number.isFinite(this.options.zIndex)) {
        this._container.style.zIndex = String(this.options.zIndex);
      }
      this._container.classList.add("leaflet-zoom-animated");

      const pane = map.getPane("tilePane") || map.getPanes().tilePane;
      pane.appendChild(this._container);
      this._syncContainerGeometry();

      this._glMap = new maplibregl.Map({
        container: this._container,
        style: this.options.style,
        interactive: false,
        attributionControl: false,
        preserveDrawingBuffer: false,
        refreshExpiredTiles: false,
        ...(Number.isFinite(this.options.pixelRatio)
          ? { pixelRatio: this.options.pixelRatio }
          : {}),
        ...(Number.isFinite(this.options.maxTileCacheSize)
          ? { maxTileCacheSize: this.options.maxTileCacheSize }
          : {}),
        ...(Number.isFinite(this.options.maxTileCacheZoomLevels)
          ? { maxTileCacheZoomLevels: this.options.maxTileCacheZoomLevels }
          : {}),
        ...(Number.isFinite(this.options.fadeDuration)
          ? { fadeDuration: this.options.fadeDuration }
          : {}),
      });

      this._glMap.on("load", () => {
        this._glReady = true;
        this._update();
        try {
          this.fire("maplibre-load");
        } catch {}
      });
      this._glMap.on("error", (err) => {
        try {
          this.fire("maplibre-error", { error: err });
        } catch {}
      });

      const canvas = this._glMap.getCanvas?.();
      if (canvas) {
        canvas.style.pointerEvents = "none";
        this._canvasContextLostHandler = (event) => {
          try {
            event?.preventDefault?.();
          } catch {}
          try {
            this.fire("maplibre-webgl-context-lost");
          } catch {}
        };
        this._canvasContextRestoredHandler = () => {
          try {
            this._glMap?.resize?.();
            this._update();
            this._glMap?.triggerRepaint?.();
          } catch {}
          try {
            this.fire("maplibre-webgl-context-restored");
          } catch {}
        };
        canvas.addEventListener("webglcontextlost", this._canvasContextLostHandler, false);
        canvas.addEventListener(
          "webglcontextrestored",
          this._canvasContextRestoredHandler,
          false
        );
      }

      this._update();

      this._visibilityHandler = () => {
        if (typeof document !== "undefined" && document.visibilityState === "hidden") {
          this.suspendRendering();
        } else {
          this.resumeRendering();
        }
      };
      document?.addEventListener?.("visibilitychange", this._visibilityHandler);
      try {
        window?.Capacitor?.Plugins?.App?.addListener?.(
          "appStateChange",
          ({ isActive }) => {
            if (isActive) this.resumeRendering();
            else this.suspendRendering();
          }
        )?.then?.((handle) => {
          this._appStateHandle = handle;
        });
      } catch {}
    },

    onRemove() {
      try {
        if (this._updateFrame != null) clearTimeout(this._updateFrame);
        if (this._settleTimer != null) clearTimeout(this._settleTimer);
      } catch {}
      this._updateFrame = null;
      this._settleTimer = null;
      try {
        document?.removeEventListener?.("visibilitychange", this._visibilityHandler);
        this._appStateHandle?.remove?.();
      } catch {}
      this._visibilityHandler = null;
      this._appStateHandle = null;
      try {
        const canvas = this._glMap?.getCanvas?.();
        if (canvas && this._canvasContextLostHandler) {
          canvas.removeEventListener("webglcontextlost", this._canvasContextLostHandler);
        }
        if (canvas && this._canvasContextRestoredHandler) {
          canvas.removeEventListener(
            "webglcontextrestored",
            this._canvasContextRestoredHandler
          );
        }
      } catch {}
      this._canvasContextLostHandler = null;
      this._canvasContextRestoredHandler = null;
      if (this._glMap) {
        this._glMap.remove();
        this._glMap = null;
      }
      if (this._container && this._container.parentNode) {
        this._container.parentNode.removeChild(this._container);
      }
      this._container = null;
      this._map = null;
    },

    suspendRendering() {
      if (this._suspended) return;
      this._suspended = true;
      try {
        this._glMap?.stop?.();
      } catch {}
    },

    resumeRendering() {
      if (!this._suspended) return;
      this._suspended = false;
      if (!this._glMap || !this._map) return;
      try {
        this._syncContainerGeometry();
        this._glMap.resize?.();
        this._update();
        this._glMap.triggerRepaint?.();
      } catch {}
    },

    getEvents() {
      return {
        move: this._update,
        moveend: this._settle,
        zoom: this._update,
        zoomend: this._settle,
        zoomanim: this._onZoomAnim,
        resize: this._resize,
        viewreset: this._settle,
      };
    },

    _syncContainerGeometry() {
      if (!this._map || !this._container) return this._containerGeometry;
      const geometry = computeContainerGeometry(
        this._map.getSize(),
        this.options.rotationOverscan === true
      );
      this._containerGeometry = geometry;
      this._container.style.width = `${geometry.width}px`;
      this._container.style.height = `${geometry.height}px`;
      const topLeft = this._map.containerPointToLayerPoint([0, 0]);
      L.DomUtil.setPosition(
        this._container,
        topLeft.subtract(L.point(geometry.padX, geometry.padY))
      );
      return geometry;
    },

    _resize() {
      if (this._suspended || !this._map || !this._container || !this._glMap) return;
      this._syncContainerGeometry();
      if (this._glReady) {
        this._glMap.resize();
        this._update();
        this._glMap.triggerRepaint?.();
      }
    },

    _settle() {
      if (
        this._suspended ||
        !this._map ||
        !this._map._loaded ||
        !this._container ||
        !this._glMap ||
        !this._glReady
      ) return;
      try {
        if (this._settleTimer != null) clearTimeout(this._settleTimer);
      } catch {}
      this._settleTimer = setTimeout(() => {
        this._settleTimer = null;
        if (
          this._suspended ||
          !this._map ||
          !this._map._loaded ||
          !this._container ||
          !this._glMap ||
          !this._glReady
        ) return;
        this._syncContainerGeometry();
        try {
          this._glMap.resize?.();
        } catch {}
        this._updateNow();
        try {
          this._glMap.triggerRepaint?.();
        } catch {}
      }, 80);
    },

    refresh() {
      if (this._suspended || !this._map || !this._container || !this._glMap) return false;
      try {
        this._syncContainerGeometry();
        const center = this._map.getCenter();
        const zoomLevel = this._map.getZoom();
        const zoom = zoomLevel - resolvedZoomOffset;
        this._glMap.resize?.();
        this._glMap.jumpTo?.({
          center: [center.lng, center.lat],
          zoom,
        });
        this._glMap.resize?.();
        this._glMap.triggerRepaint?.();
        this._center = center;
        this._zoom = zoomLevel;
        return true;
      } catch {
        return false;
      }
    },

    _update() {
      if (
        this._suspended ||
        !this._map ||
        !this._map._loaded ||
        !this._container ||
        !this._glMap ||
        !this._glReady
      ) return;
      if (this._map._animatingZoom) return;
      // Leaflet emits both `move` and `zoom` for a two-finger pan/pinch.
      // Coalesce those duplicate events: resizing/jumping MapLibre for every
      // touch sample can overwhelm a mobile WebView/GPU process.
      if (this._updateFrame != null) return;
      const interval = Math.max(
        0,
        Number(this.options.interactionUpdateInterval) || 0
      );
      const elapsed = Date.now() - this._lastInteractionUpdateAt;
      const delay = Math.max(0, interval - elapsed);
      this._updateFrame = setTimeout(() => {
        this._updateFrame = null;
        this._lastInteractionUpdateAt = Date.now();
        this._updateNow();
      }, delay);
    },

    _updateNow() {
      if (
        this._suspended ||
        !this._map ||
        !this._map._loaded ||
        !this._container ||
        !this._glMap ||
        !this._glReady
      ) return;
      this._syncContainerGeometry();

      const center = this._map.getCenter();
      const zoomLevel = this._map.getZoom();
      this._center = center;
      this._zoom = zoomLevel;
      const zoom = zoomLevel - resolvedZoomOffset;

      try {
        this._glMap.jumpTo({
          center: [center.lng, center.lat],
          zoom,
        });
      } catch {}
    },

    _onZoomAnim(e) {
      if (this._suspended || !this._map || !this._container || !this._glMap || !this._glReady) return;
      const baseZoom = Number.isFinite(this._zoom) ? this._zoom : this._map.getZoom();
      const baseCenter = this._center || this._map.getCenter();
      const scale = this._map.getZoomScale(e.zoom, baseZoom);
      const viewHalf = this._map.getSize().multiplyBy(0.5);
      const currentCenterPoint = this._map.project(baseCenter, e.zoom);
      const topLeftOffset = viewHalf
        .multiplyBy(-scale)
        .add(currentCenterPoint)
        .subtract(this._map._getNewPixelOrigin(e.center, e.zoom));
      const geometry = this._containerGeometry || { padX: 0, padY: 0 };
      L.DomUtil.setTransform(
        this._container,
        topLeftOffset.subtract(L.point(geometry.padX, geometry.padY)),
        scale
      );
    },
  });

  return new MapLibreLayer({
    style,
    className,
    zoomOffset: resolvedZoomOffset,
    rotationOverscan,
    pixelRatio,
    maxTileCacheSize,
    maxTileCacheZoomLevels,
    fadeDuration,
    interactionUpdateInterval,
    zIndex,
  });
}
